/**
 * detectOverallExperienceDuplicate
 *
 * P_SURVEY_EDITOR_UX_ENHANCEMENT_1.1
 *
 * Soft heuristic used by the Survey Editor to inform authors that
 * their first custom question may be duplicating the built-in
 * Overall Experience screen. Informational only — never blocks save
 * and never modifies the survey.
 *
 * Returns `true` when ALL of:
 *   - the survey has overall_experience_enabled !== false; AND
 *   - the first question exists and is enabled; AND
 *   - the first question's text matches an "overall experience"
 *     pattern in EN, FR or ES, OR it is a 1-5 star/rating question
 *     (which is overwhelmingly used for this exact purpose).
 *
 * Patterns intentionally kept tight to avoid false positives on
 * legitimate first questions like "What's your name?" or
 * "Which location are you visiting?".
 */

// Lower-cased substrings that strongly signal an "overall experience"
// intent. Each entry is a substring — we lowercase the question text
// before matching, so case doesn't matter.
const OVERALL_PATTERNS = [
    // ── English ──
    'overall experience',
    'overall rating',
    'rate your experience',
    'rate your overall',
    'how was your experience',
    'how was your visit',
    'how would you rate your overall',
    'how was your overall',
    // ── French ──
    'expérience globale',
    'expérience générale',
    'évaluation globale',
    'évaluez votre expérience',
    'évaluez votre visite',
    'comment évalueriez-vous votre expérience',
    'comment était votre expérience',
    'comment était votre visite',
    // ── Spanish ──
    'experiencia general',
    'experiencia global',
    'calificación general',
    'califique su experiencia',
    'califique su visita',
    'cómo fue su experiencia',
    'cómo fue su visita',
    'cómo calificaría su experiencia',
];

function _textInAnyLang(text) {
    if (!text || typeof text !== 'object') return '';
    return [text.en, text.fr, text.es]
        .filter(Boolean)
        .map((s) => String(s).toLowerCase())
        .join(' \u2002 ');  // joined with em-space so substrings don't merge across languages
}

function matchesOverallText(q) {
    const joined = _textInAnyLang(q?.text);
    if (!joined) return false;
    return OVERALL_PATTERNS.some((p) => joined.includes(p));
}

function isOneToFiveRating(q) {
    if (!q || q.response_type !== 'rating') return false;
    const scale = q.rating_scale || {};
    const min = Number(scale.min ?? 1);
    const max = Number(scale.max ?? 5);
    return min === 1 && max === 5;
}

export function detectOverallExperienceDuplicate(survey) {
    if (!survey) return false;
    if (survey.overall_experience_enabled === false) return false;
    const q1 = (survey.questions || []).find((q) => q && q.enabled !== false);
    // Note: skipping disabled questions; only consider the first visible Q.
    if (!q1) return false;
    return matchesOverallText(q1) || isOneToFiveRating(q1);
}

export default detectOverallExperienceDuplicate;
